<template>
  <div>
    <div v-if="showNullContainer">
      <div :class="$style.nullContainer">
        <section class="section">
          <div class="section content has-text-grey has-text-centered">
            <p>
              <span class="icon is-large">
                <i class="mdi mdi-alert-circle-outline mdi-48px"></i>
              </span>
            </p>
            <p class="bold">No GRiP analysis</p>
            <p>
              To create a GRiP analysis, enable GRiP under
              <a
                v-if="auditId"
                :class="$style.button"
                :href="`/projects/${projectId}#settings`"
              >
                Project Settings.
              </a>
              <a v-else :class="$style.button" @click="handleNavigate()"
                >Settings.</a
              >
            </p>
          </div>
        </section>
      </div>
    </div>
    <div v-else :class="$style.container">
      <div :class="$style.headerContainer">
        <p :class="$style.headerTitle">GRiP Report</p>
        <button
          class="button is-large is-rounded is-primary bold"
          @click="handleExport()"
          :disabled="isExportDisabled"
        >
          Export
        </button>
      </div>
      <br />
      <div
        v-if="isDataCollector && !isGripDetails"
        :class="$style.gripContainer"
      >
        <div :class="$style.nullTeamDetailsContainer">
          <p>No GRiP Report Available</p>
        </div>
      </div>
      <div v-else>
        <div :class="$style.gripContainer">
          <BaseLoading
            v-if="isGripDetailStateLoading"
            class="background-white"
          />
          <GripTeamDetailCard
            v-else-if="showGripDetailsContainer"
            :gripDetails="gripDetails"
            :projectId="projectId"
            :isDataCollector="isDataCollector"
            @createGrip="handleCreateGrip"
            @updateGrip="handleUpdateGrip"
          />

          <div v-else :class="$style.nullTeamDetailsContainer">
            <a
              v-if="!isDataCollector"
              :class="$style.addNewItem"
              @click="handleAddGripTeamDetails()"
            >
              Enter GRiP Project Team Details
            </a>
          </div>
        </div>
        <div :class="$style.gripHorizontalPaneContainer">
          <GripAnalysisSideBar
            v-if="!isGripAnalysisLoading && !isDataCollector"
            :key="analysisSideBarKey"
            :projectId="projectId"
            :isSideBarOpen.sync="isSideBarOpen"
            :analysisCriteriaIds="analysisCriteriaIds"
            :isGripAnalysis="isGripAnalysis"
            :activeSidebarTab="activeSidebarTabIndex"
            @updateSelectedCriteria="handleSelectedCriteria"
            @collapseSideNav="handleSideBarCollapse"
            @updateActiveSidebarTab="handleActiveSidebarTab"
            :class="[
              $style.sideBarContainer,
              isSideBarOpen ? 'column is-one-third no-padding' : null,
            ]"
          />
          <BaseLoading
            v-if="isGripAnalysisListLoading"
            class="background-white"
          />
          <GripAnalysisPane
            v-if="!isGripAnalysisListLoading"
            :isDataCollector="isDataCollector"
            :projectId="projectId"
            :isSideBarOpen.sync="isSideBarOpen"
            :isGripDetails="isGripDetails"
            :gripAnalysisList="gripAnalysisList"
            :selectedAnalysisTabIndex="selectedAnalysisTabIndex"
            @updateGripAnalysisList="handleUpdatedGripAnalysisList"
            @renameGripAnalysis="handleRenameGripAnalysis"
            @gripAnalysisCriteria="handleGripAnalysisCriteria"
            :class="[
              isDataCollector
                ? $style.dataCollectorAnalysisContainer
                : $style.analysisContainer,
            ]"
            @addToProjectGrip="handleAddedToProjectGrip"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import BaseLoading from '../../components/base/BaseLoading.vue';
import { ApiState } from '../../store/types/general.types';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  Grip,
  GripDTO,
  CreateGripPayload,
  UpdateGripPayload,
  UpdateGripAnalysisPayload,
  GripAnalysis,
  GripAnalysisCriteria,
  RenameGripAnalysisPayload,
} from '../../store/types/grip.types';
import GripAnalysisPane from './components/GripAnalysisPane.vue';
import GripAnalysisSideBar from './components/GripAnalysisSideBar.vue';
import GripTeamDetailCard from './components/GripTeamDetailCard.vue';
import {
  ProjectCriteria,
  ProjectDetailPayload,
} from '../../store/modules/projects/types/projects.types';
import ExportGripReportModal from './components/ExportGripReportModal.vue';
@Component({
  components: {
    GripTeamDetailCard,
    BaseLoading,
    GripAnalysisPane,
    GripAnalysisSideBar,
    ExportGripReportModal,
  },
})
export default class GripTab extends Vue {
  @Prop() public projectId!: number;
  @Prop() public projectDetail!: ProjectDetailPayload;
  @Prop() public isDataCollector!: boolean;

  public isSideBarOpen: boolean = true;
  public showNullContainer: boolean = true;
  public showGripDetailsContainer: boolean = false;
  public isGripDetails: boolean = true;
  public isGripAnalysis: boolean = false;
  public isGripAnalysisListLoading: boolean = false;
  public isGripAnalysisLoading: boolean = false;
  public isGripDetailStateLoading: boolean = false;
  public isGripAnalysisCriteriaUpdated: boolean = false;
  public selectedProjectCriteriaList: ProjectCriteria[] = [];
  public analysisCriteriaIds: number[] = [];
  public analysisPaneKey: number = Math.floor(Math.random() * 999);
  public analysisSideBarKey: number = Math.floor(Math.random() * 999);
  public selectedAnalysisTabIndex: number = 0;
  public activeSidebarTabIndex: number = 1;

  @State((state) => state.projects.projectCriteria)
  public projectCriteria!: ProjectCriteria[];

  @Action('projects/getGrip')
  public getGrip!: (projectId: number) => void;

  @State((state) => state.projects.gripDetails)
  public gripDetails!: Grip;

  @State((state) => state.projects.apiState.getGripDetails)
  public getGripDetailState!: ApiState;

  @Action('projects/createGrip')
  public createGrip!: (payload: CreateGripPayload) => void;

  @State((state) => state.projects.apiState.createGripDetails)
  public createGripDetailState!: ApiState;

  @State((state) => state.projects.createdGripDetails)
  public createGripDetails!: Grip;

  @Action('projects/updateGrip')
  public updateGrip!: (payload: UpdateGripPayload) => void;

  @State((state) => state.projects.updatedGripDetails)
  public updatedGripDetails!: Grip;

  @State((state) => state.projects.apiState.updateGripDetails)
  public updateGripDetailState!: ApiState;

  @Action('projects/getGripAnalysisList')
  public getGripAnalysisList!: (projectId: number) => void;

  @State((state) => state.projects.gripAnalysisList)
  public gripAnalysisList!: GripAnalysis[];

  @State((state) => state.projects.apiState.getGripAnalysisList)
  public getGripAnalysisListState!: ApiState;

  @Action('projects/getGripAnalysis')
  public getGripAnalysis!: (payload: {
    projectId: number;
    gripAnalysisId: number;
  }) => void;

  @State((state) => state.projects.gripAnalysis)
  public gripAnalysis!: GripAnalysis;

  @Action('projects/updateGripAnalysis')
  public updateGripAnalysis!: (payload: {
    projectId: number;
    payload: UpdateGripAnalysisPayload;
  }) => void;

  @State((state) => state.projects.apiState.updateGripAnalysis)
  public updateGripAnalysisState!: ApiState;

  @State((state) => state.projects.updatedGripAnalysis)
  public updatedGripAnalysis!: any;

  @Action('projects/renameGripAnalysis')
  public renameGripAnalysis!: (payload: {
    projectId: number;
    payload: RenameGripAnalysisPayload;
  }) => void;

  @State((state) => state.projects.apiState.renameGripAnalysis)
  public renameGripAnalysisState!: ApiState;

  get auditId(): number {
    return +this.$route.params.auditId;
  }

  get isExportDisabled(): boolean {
    return this.isGripDetails ? false : true;
  }

  public handleSideBarCollapse(isOpen: boolean): void {
    this.isSideBarOpen = isOpen;
  }

  public handleActiveSidebarTab(tabIndex: number): void {
    this.activeSidebarTabIndex = tabIndex;
  }

  public handleCreateGrip(gripDetails: GripDTO): void {
    this.createGrip({
      projectLead: gripDetails.projectLead,
      stakeholder: gripDetails.stakeholder,
      actionDate: gripDetails.actionDate,
      projectId: this.projectId,
    });
  }

  public handleUpdateGrip(gripDetails: UpdateGripPayload): void {
    this.updateGrip({
      id: gripDetails.id,
      projectLead: gripDetails.projectLead,
      stakeholder: gripDetails.stakeholder,
      actionDate: gripDetails.actionDate,
      projectId: this.projectId,
    });
  }

  public handleSelectedCriteria(data: {
    selectedCriteriaIds: number[];
    update: boolean;
  }): void {
    const selectedProjectCriteria: ProjectCriteria[] = this.projectCriteria
      ? this.projectCriteria.filter((criteria) =>
          data.selectedCriteriaIds.includes(criteria.id),
        )
      : [];

    const selectedCriteriaIds: number[] = selectedProjectCriteria.map(
      (criteria) => criteria.id,
    );
    if (data.update) {
      this.isGripAnalysisCriteriaUpdated = true;
      this.updateGripAnalysis({
        projectId: this.projectId,
        payload: {
          gripAnalysisId: this.gripAnalysis.id,
          criteriaIds: selectedCriteriaIds,
        },
      });
    }
  }

  public handleUpdatedGripAnalysisList(selectedAnalysisTab: number): void {
    this.selectedAnalysisTabIndex = selectedAnalysisTab;
    this.isGripAnalysisListLoading = true;
    this.getGripAnalysisList(this.projectId);
  }

  public handleGripAnalysisCriteria(
    gripAnalysisCriteriaMaps: GripAnalysisCriteria[],
    isGripAnalysisTabChange: boolean,
  ): void {
    if (this.isGripAnalysisCriteriaUpdated || isGripAnalysisTabChange) {
      this.isGripAnalysisCriteriaUpdated = false;
      this.analysisCriteriaIds = gripAnalysisCriteriaMaps.map(
        (gripAnalysisCriteria: GripAnalysisCriteria) =>
          gripAnalysisCriteria.criteria.id,
      );
      this.analysisSideBarKey += 1;
    }
  }

  public handleAddedToProjectGrip(item: string): void {
    const sideBarMenu: string[] = [
      'criteria',
      'barrier',
      'enabler',
      'strategy',
    ];
    const findIndex: number = sideBarMenu.findIndex((menu) => menu === item);
    if (this.activeSidebarTabIndex === findIndex + 1) {
      this.analysisSideBarKey += 1;
    }
  }

  public handleRenameGripAnalysis(
    renamePayload: RenameGripAnalysisPayload,
  ): void {
    this.renameGripAnalysis({
      projectId: this.projectId,
      payload: renamePayload,
    });
  }

  public handleNavigate(): void {
    this.$emit('changeActiveTab', '#settings');
  }

  public handleExport(): void {
    this.$buefy.modal.open({
      parent: this,
      component: ExportGripReportModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        projectId: this.projectId,
      },
      width: 1140,
      events: {
        close: () => {
          this.$emit('close');
        },
      },
    });
  }

  public handleAddGripTeamDetails(): void {
    this.showGripDetailsContainer = true;
  }

  public mounted(): void {
    this.showNullContainer =
      this.projectDetail && this.projectDetail.enableProjectGrip ? false : true;
    if (!this.showNullContainer) {
      this.isGripDetailStateLoading = true;
      this.getGrip(this.projectId);
      this.getGripAnalysisList(this.projectId);
    }
  }

  @Watch('getGripDetailState')
  public watchGetGripDetailState(state: ApiState): void {
    if (state.loading) {
      this.isGripDetailStateLoading = true;
    } else {
      this.isGripDetailStateLoading = false;
      if (state.success) {
        if (this.gripDetails?.id != null) {
          this.showGripDetailsContainer = true;
          this.isGripDetails = true;
        } else {
          this.showGripDetailsContainer = false;
          this.isGripDetails = false;
        }
      }
    }
  }

  @Watch('createGripDetailState')
  public watchCreateGripDetailState(state: ApiState): void {
    if (state.success) {
      this.getGrip(this.projectId);
      Toast.open({
        message: `GRiP team details added successfully.`,
        position: 'is-top',
        type: 'is-dark',
        duration: 3500,
      });
    } else if (state.error) {
      Toast.open({
        message: `Something went wrong. Try again.`,
        position: 'is-top',
        type: 'is-danger',
        duration: 3500,
      });
    }
  }

  @Watch('updateGripDetailState')
  public watchUpdateGripDetailState(state: ApiState): void {
    if (state.success) {
      this.getGrip(this.projectId);
      Toast.open({
        message: `Updated successfully.`,
        position: 'is-top',
        type: 'is-dark',
        duration: 3500,
      });
    } else if (state.error) {
      Toast.open({
        message: `Something went wrong. Try again.`,
        position: 'is-top',
        type: 'is-danger',
        duration: 3500,
      });
    }
  }

  @Watch('getGripAnalysisListState')
  public watchGetGripAnalysisListState(state: ApiState): void {
    if (state.loading) {
      this.isGripAnalysisListLoading = true;
    } else {
      this.isGripAnalysisListLoading = false;
      if (state.success) {
        this.isGripAnalysis = this.gripAnalysisList.length > 0;
      }
    }
  }

  @Watch('updateGripAnalysisState')
  public watchUpdateGripAnalysisState(state: ApiState): void {
    if (state.success) {
      Toast.open({
        message: `Analysis updated successfully.`,
        position: 'is-top',
        type: 'is-dark',
        duration: 3500,
      });
      this.getGripAnalysis({
        projectId: this.projectId,
        gripAnalysisId: this.gripAnalysis.id,
      });
    } else if (state.error) {
      Toast.open({
        message: `Something went wrong. Try again.`,
        position: 'is-top',
        type: 'is-danger',
        duration: 3500,
      });
    }
  }

  @Watch('renameGripAnalysisState')
  public watchRenameGripAnalysisState(state: ApiState): void {
    if (state.success) {
      Toast.open({
        message: `Analysis renamed successfully.`,
        position: 'is-top',
        type: 'is-dark',
        duration: 3500,
      });
      this.getGripAnalysisList(this.projectId);
    } else if (state.error) {
      Toast.open({
        message: `Something went wrong. Try again.`,
        position: 'is-top',
        type: 'is-danger',
        duration: 3500,
      });
    }
  }
}
</script>
<style lang="scss" module>
.container {
  padding: 0.75rem 0rem 0rem 0rem;
}

.sideBarContainer {
  background: #21b5ea;
}
.gripContainer {
  display: flex;
  flex-direction: column;
  padding: 0px;
  width: 100%;
  background: #ffffff;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}
.headerContainer {
  display: flex;
  flex-direction: row;
  background-color: #fbfbfb;
  .headerTitle {
    flex-grow: 1;
    line-break: auto;
    color: #444f51;
    font-size: 24px;
    font-weight: bold;
    text-align: left;
    padding: 20px 0;
  }
}
.nullContainer {
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-bottom: 1rem;
  box-shadow: 0 2px 2px $grey-border;
  border: 0.5px solid $grey-border;
  border-radius: 6px;
  background: #ffffff;
  overflow-x: auto;
}
.analysisContainer {
  background: white;
  padding: 0;
  flex-direction: column;
  min-height: 720px;
  width: 100%;
  border-radius: 0;
  margin-bottom: 0;
  margin-top: 0;
  box-shadow: none;
}

.dataCollectorAnalysisContainer {
  background: white;
  padding: 0;
  flex-direction: column;
  min-height: auto;
  width: 100%;
  border-radius: 0;
  margin-bottom: 0;
  margin-top: 0;
  box-shadow: none;
}

.button {
  border: none;
  background: transparent;
  color: #21b5ea;
  font-size: 16px;
  cursor: pointer;
}

.nullTeamDetailsContainer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  background: white;
  padding: 6rem 1.5rem;
}

.addNewItem {
  color: #00205b;
  font-weight: bold;
  font-size: 14px;
}

.gripHorizontalPaneContainer {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  background: #ffffff;
  border-top: 1px solid #dfdfdf;
  border-bottom: 1px solid #dfdfdf;
  border-right: 1px solid #dfdfdf;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.12);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
</style>
